import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Users,
  Zap,
  Shield,
  BarChart2,
  ArrowRight,
  Star,
  Code2,
} from "lucide-react";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import Footer from "../../components/Footer";
import api from "../../api/client";

export default function AgencyLanding() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "Joborigo for Agencies: Place candidates faster.";
    api
      .get("/public/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 sticky top-0 z-30 bg-white dark:bg-brand-dark-bg">
        <Logo className="h-12" />
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <Link
            to="/employers/how-it-works"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            For employers &rarr;
          </Link>
          <Link
            to="/employers/login"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            Sign in
          </Link>
          <Link
            to="/employers/register"
            className="text-sm bg-brand-violet text-white px-4 py-1.5 rounded-md hover:opacity-90 transition"
          >
            Get started free
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-brand-light-bg dark:bg-brand-dark-bg py-24 px-6 text-center">
        <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
          For recruitment agencies
        </p>
        <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4 max-w-2xl mx-auto leading-tight">
          Place candidates faster.
          <br />
          Keep them informed automatically.
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
          Joborigo removes the manual update burden and gives your candidates a
          verified, real-time view of their hiring process, straight from you,
          without the endless check-in calls.
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/employers/register"
            className="bg-brand-violet text-white px-8 py-3 rounded-lg text-lg font-medium hover:opacity-90 transition"
          >
            Register as an agency
          </Link>
          <a
            href="mailto:agencies@joborigo.com"
            className="bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 px-8 py-3 rounded-lg text-lg font-medium hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            Contact us
          </a>
        </div>
      </section>

      {/* Founding Partner Banner */}
      <section className="bg-brand-violet py-6 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">
                Founding Agency Partner offer
              </p>
              <p className="text-white/70 text-xs">
                Founding partners get 50% off for the first year, plus direct
                input into the product roadmap.
              </p>
            </div>
          </div>
          <a
            href="mailto:agencies@joborigo.com"
            className="flex-shrink-0 bg-white text-brand-violet dark:text-brand-violet-on-dark px-5 py-2 rounded-lg text-sm font-medium hover:bg-white/90 transition"
          >
            Apply for founding partner &rarr;
          </a>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="bg-brand-violet dark:bg-brand-dark-surface py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-white/60 text-sm text-center mb-10 uppercase tracking-widest font-medium">
              Joborigo by the numbers
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {[
                {
                  value: stats.total_seekers,
                  label: "Job seekers tracking applications",
                },
                {
                  value: stats.total_applications,
                  label: "Applications being tracked",
                },
                {
                  value: stats.arena_profiles,
                  label: "Candidates in the Arena",
                },
                {
                  value: stats.total_status_pushes,
                  label: "Real-time status updates pushed",
                },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-4xl font-bold text-white mb-1">
                    {s.value.toLocaleString()}
                  </p>
                  <p className="text-white/60 text-xs leading-relaxed">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What is Joborigo for agencies */}
      <section className="py-20 px-6 bg-white dark:bg-brand-dark-bg">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
              What you get
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              Built for agencies who care about candidate experience
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-sm leading-relaxed">
              Your candidates deserve to know what's happening. Joborigo gives
              them a real-time view of their process, and gives you a
              verifiable track record of placements.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                icon: Zap,
                title: "Automatic candidate updates",
                desc: "Push real-time status updates directly to your candidates. No more manual check-in calls. No more 'just following up' emails. Candidates see their process as it happens.",
              },
              {
                icon: Users,
                title: "Browse the candidate arena",
                desc: "Access Joborigo's anonymous candidate marketplace. Filter by skills, availability, location and salary. Send job positions directly to matched profiles.",
              },
              {
                icon: Shield,
                title: "Privacy by default",
                desc: "Candidates share a connect token, not their email. Your integration is direct and private. No third-party exposure, no data leakage.",
              },
              {
                icon: BarChart2,
                title: "Verifiable placement history",
                desc: "Every process you run through Joborigo creates a verified record. Build a track record of transparent, communicative placements that candidates remember.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-5 bg-gray-50 dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10"
              >
                <div className="w-9 h-9 rounded-xl bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-brand-dark-surface">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
              How it works
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              Simple to set up. Powerful in practice.
            </h2>
          </div>
          <div className="flex flex-col gap-8">
            {[
              {
                step: "01",
                title: "Register as an agency",
                desc: "Create your account at joborigo.com/employers/register and enable the agency toggle. Generate an API key from your dashboard.",
              },
              {
                step: "02",
                title: "Browse or bring your own candidates",
                desc: "Use the Arena to find candidates who have opted in, or work with your existing candidate pool. Connect them via the integrate endpoint.",
              },
              {
                step: "03",
                title: "Submit candidates to employers",
                desc: "Send candidates to employers with your agency ID embedded. The employer sees a Joborigo-tagged candidate link and can connect their hiring system.",
              },
              {
                step: "04",
                title: "Push updates as the process progresses",
                desc: "Every time the hiring status changes, push an update. Candidates see it immediately. You build a reputation for transparency with every placement.",
              },
            ].map((step) => (
              <div key={step.step} className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-brand-violet dark:text-brand-violet-on-dark">
                    {step.step}
                  </span>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API card */}
      <section className="py-20 px-6 bg-white dark:bg-brand-dark-bg">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
              Developer docs
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              One API for the full placement flow
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-sm leading-relaxed">
              The Joborigo Arena API covers everything: browsing candidates,
              sending positions, tracking responses, and viewing the full
              process history.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              {
                icon: Code2,
                title: "Arena API",
                desc: "Browse anonymous candidates, send job positions, track sent positions and candidate responses, view full process history.",
                link: "/employers/arena-api",
                label: "View Arena API docs",
                badge: "For proactive sourcing",
                badgeColor:
                  "bg-brand-violet-tint text-brand-violet dark:bg-brand-violet/20 dark:text-brand-violet-on-dark",
              },
              {
                icon: Zap,
                title: "ATS / Status push API",
                desc: "Push real-time status updates and structured correspondence such as meeting invites, document requests and assessments, delivered directly to candidates.",
                link: "/employers/api",
                label: "View ATS API docs",
                badge: "For status updates",
                badgeColor:
                  "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="flex flex-col p-6 bg-gray-50 dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                    <card.icon className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {card.title}
                    </p>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${card.badgeColor}`}
                    >
                      {card.badge}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 flex-1">
                  {card.desc}
                </p>
                <Link
                  to={card.link}
                  className="flex items-center gap-2 text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  {card.label} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Partner CTA */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-brand-dark-surface">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-2xl bg-brand-violet p-px">
            <div className="rounded-2xl bg-white dark:bg-brand-dark-bg px-8 py-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-brand-violet/10 flex items-center justify-center mx-auto mb-4">
                <Star className="w-6 h-6 text-brand-violet dark:text-brand-violet-on-dark" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Become a Founding Agency Partner
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-2 leading-relaxed">
                We are working with a small number of agencies in an early phase,
                partners who want to shape how transparent recruitment works.
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 leading-relaxed">
                You get{" "}
                <span className="font-semibold text-brand-violet dark:text-brand-violet-on-dark">
                  50% off for your first year
                </span>{" "}
                and direct input into our product roadmap. We get a partner who
                cares about candidate experience as much as we do.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <a
                  href="mailto:agencies@joborigo.com"
                  className="bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
                >
                  Apply for founding partner
                </a>
                <Link
                  to="/employers/register"
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                >
                  Or register now →
                </Link>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Questions?{" "}
                <a
                  href="mailto:agencies@joborigo.com"
                  className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  agencies@joborigo.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
